import { useEffect, useMemo, useState } from "react"
import { useQuery } from "react-query"
import styled from "styled-components"
import moment from "moment"
import { rgba } from "polished"

import PageContent from "~components/PageContent"
import PageHeader from "~components/PageHeader"
import useProofsOfOwnership from "~hooks/useProofsOfOwnership"
import useFinalizerLabel from "~hooks/useFinalizerLabel"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"
import { PROOF_OF_OWNERSHIP_STATUS } from "~constants/proofOfOwnership"
import ProofOfOwnershipViewer from "~components/proofOfOwnership/ProofOfOwnershipViewer"
import ProofOfOwnershipListItem from "~components/proofOfOwnership/ProofOfOwnershipListItem"
import ProofOfOwnershipService from "~services/ProofOfOwnershipService"
import colors from "~constants/colors"

// Secondary, read-only history of completed POOs. Each tab is a clean single-state
// infinite query (newest-first), so this never touches the Created / My validations
// queues. Validated and Invalidated are split into tabs rather than merged, which
// keeps cursor pagination simple and correct.
const FILTERS = [
  {
    state: PROOF_OF_OWNERSHIP_STATUS.VALIDATED,
    label: "Validated",
    color: colors.green,
  },
  {
    state: PROOF_OF_OWNERSHIP_STATUS.INVALIDATED,
    label: "Invalidated",
    color: colors.red,
  },
] as const

const PooHistoryPage = () => {
  const [activeState, setActiveState] = useState<PROOF_OF_OWNERSHIP_STATUS>(
    PROOF_OF_OWNERSHIP_STATUS.VALIDATED,
  )
  const [selectedPoo, setSelectedPoo] = useState<ProofOfOwnership | null>(null)
  const [sort, setSort] = useState("created_at,desc")

  const {
    data: pooList,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    // Operator-wide (no user filter). The backend orders by created_at on the
    // stateAndCreatedAtIndex GSI, so the `sort` toggle is all that's needed —
    // no client-side re-sort.
  } = useProofsOfOwnership("", activeState, true, sort)

  const pooItems = useMemo(() => pooList ?? [], [pooList])

  // Tab badge counts — one indexed COUNT query per state, cached. Undefined while
  // loading (rendered as "…"); a failed count resolves to 0, never blanks the UI.
  const { data: counts } = useQuery(
    ["poo-history-counts"],
    async () => {
      const [validated, invalidated] = await Promise.all([
        ProofOfOwnershipService.getProofOfOwnershipStateCount(
          PROOF_OF_OWNERSHIP_STATUS.VALIDATED,
        ),
        ProofOfOwnershipService.getProofOfOwnershipStateCount(
          PROOF_OF_OWNERSHIP_STATUS.INVALIDATED,
        ),
      ])
      return {
        [PROOF_OF_OWNERSHIP_STATUS.VALIDATED]: validated,
        [PROOF_OF_OWNERSHIP_STATUS.INVALIDATED]: invalidated,
      } as Record<string, number>
    },
    { staleTime: 5 * 60 * 1000 },
  )

  // Reset the selection when the tab changes so a Validated detail doesn't linger
  // over the Invalidated list while it loads.
  useEffect(() => {
    setSelectedPoo(null)
  }, [activeState])

  // Auto-select the newest item once a list loads.
  useEffect(() => {
    if (pooItems.length > 0 && !selectedPoo) {
      setSelectedPoo(pooItems[0])
    }
  }, [pooItems, selectedPoo])

  // Who delivered the verdict (operator email / "System · auto-expirer").
  const finalizerLabel = useFinalizerLabel(
    selectedPoo?.finalized_by_type,
    selectedPoo?.finalized_by,
  )

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 80) fetchNextPage()
  }

  const activeFilter = FILTERS.find(f => f.state === activeState)!

  return (
    <PageContent>
      <PageHeader
        title="Validation history"
        subtitle="Completed proofs of ownership — validated and invalidated — across all users, including who delivered the verdict. Read-only; separate from the Created and My validations queues."
      />

      <Controls>
        <Segmented>
          {FILTERS.map(f => {
            const count = counts?.[f.state]
            return (
              <SegmentBtn
                key={f.state}
                $active={f.state === activeState}
                onClick={() => setActiveState(f.state)}
              >
                {f.label}
                <Count $active={f.state === activeState}>
                  {count === undefined ? "…" : count}
                </Count>
              </SegmentBtn>
            )
          })}
        </Segmented>

        <SortControl>
          <SortLabel htmlFor="poo-history-sort">Sort</SortLabel>
          <SortSelect
            id="poo-history-sort"
            value={sort}
            onChange={e => setSort(e.target.value)}
          >
            <option value="created_at,desc">Newest first</option>
            <option value="created_at,asc">Oldest first</option>
          </SortSelect>
        </SortControl>
      </Controls>

      <ContentLayout>
        <ListPanel>
          <ListHeader>
            <span>{pooItems.length} items</span>
          </ListHeader>
          <ListScroll onScroll={handleListScroll}>
            {isFetching && pooItems.length === 0 ? (
              <Hint>Loading…</Hint>
            ) : isError ? (
              <Hint>Couldn't load the history.</Hint>
            ) : pooItems.length === 0 ? (
              <Hint>No {activeFilter.label.toLowerCase()} proofs yet.</Hint>
            ) : (
              <>
                {pooItems.map(el => (
                  <ProofOfOwnershipListItem
                    key={el.id}
                    selectedItem={
                      selectedPoo ?? ({ id: "" } as ProofOfOwnership)
                    }
                    onClick={poo => setSelectedPoo(poo)}
                    item={el}
                  />
                ))}
                {isFetchingNextPage && <Hint>Loading…</Hint>}
              </>
            )}
          </ListScroll>
        </ListPanel>

        <ViewerPanel>
          {selectedPoo && (
            <DetailBar>
              <StatusChip $color={activeFilter.color}>
                {activeFilter.label}
              </StatusChip>
              <DetailItem>
                <DetailLabel>Broker</DetailLabel>
                <DetailValue>{selectedPoo.broker || "—"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Name</DetailLabel>
                <DetailValue>{selectedPoo.name || "—"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Uploaded</DetailLabel>
                <DetailValue>
                  {selectedPoo.created_at
                    ? moment(selectedPoo.created_at).format("MMM D, YYYY")
                    : "—"}
                </DetailValue>
              </DetailItem>
              {selectedPoo.updated_at && (
                <DetailItem>
                  <DetailLabel>Finalized</DetailLabel>
                  <DetailValue>
                    {moment(
                      selectedPoo.updated_at,
                      "YYYY-MM-DD HH:mm:ss",
                    ).format("MMM D, YYYY")}
                  </DetailValue>
                </DetailItem>
              )}
              <DetailItem>
                <DetailLabel>Finalized by</DetailLabel>
                <DetailValue>{finalizerLabel || "—"}</DetailValue>
              </DetailItem>
              {activeState === PROOF_OF_OWNERSHIP_STATUS.INVALIDATED &&
                selectedPoo.invalidation_reason && (
                  <DetailItem>
                    <DetailLabel>Reason</DetailLabel>
                    <DetailValue>{selectedPoo.invalidation_reason}</DetailValue>
                  </DetailItem>
                )}
            </DetailBar>
          )}
          <ViewerWrapper $hasDetailBar={!!selectedPoo}>
            {selectedPoo ? (
              <ProofOfOwnershipViewer
                isLoadingList={isFetching}
                item={selectedPoo}
              />
            ) : (
              <ViewerPlaceholder>
                Select a proof of ownership to preview the document.
              </ViewerPlaceholder>
            )}
          </ViewerWrapper>
        </ViewerPanel>
      </ContentLayout>
    </PageContent>
  )
}

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`

const Segmented = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid ${colors.gray2};
  border-radius: 10px;
  background: ${colors.white};
`

const Count = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 8px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 700;
  color: ${p => (p.$active ? colors.white : colors.gray6)};
  background: ${p =>
    p.$active ? rgba(colors.white, 0.25) : rgba(colors.gray6, 0.12)};
`

const SortControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SortLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${colors.gray6};
`

const SortSelect = styled.select`
  height: 32px;
  padding: 0 10px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.gray3};
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  background: ${colors.white};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.blue1};
  }
`

const SegmentBtn = styled.button<{ $active: boolean }>`
  height: 32px;
  padding: 0 16px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  color: ${p => (p.$active ? colors.white : colors.gray6)};
  background: ${p => (p.$active ? colors.blue1 : "transparent")};
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${p => (p.$active ? colors.blue1 : rgba(colors.blue2, 0.1))};
  }
`

const ContentLayout = styled.div`
  display: flex;
  gap: 12px;
  height: 560px;
`

const ListPanel = styled.div`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.gray2};
  border-radius: 10px;
  background: ${colors.white};
  overflow: hidden;
`

const ListHeader = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${colors.gray2};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.gray6};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

const ListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px;
`

const Hint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 12px;
  color: ${colors.gray6};
  font-size: 13px;
  text-align: center;
`

const ViewerPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const DetailBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  background: ${colors.white};
  border: 1px solid ${colors.gray2};
  border-radius: 10px 10px 0 0;
  border-bottom: none;
  flex-shrink: 0;
  flex-wrap: wrap;
`

const StatusChip = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.white};
  background: ${p => p.$color};
  white-space: nowrap;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const DetailLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${colors.gray6};
`

const DetailValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.gray3};
`

const ViewerWrapper = styled.div<{ $hasDetailBar?: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${p => (p.$hasDetailBar ? "0 0 10px 10px" : "10px")};
`

const ViewerPlaceholder = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 1px solid ${colors.gray2};
  border-radius: 10px;
  color: ${colors.gray6};
  font-size: 14px;
  text-align: center;
`

export default PooHistoryPage
